import { Button, Flex, Group, Text } from '@mantine/core'
import { UseFormReturnType } from '@mantine/form/lib/types'
import PolicyCheckHOC from '@v2/commons/HOC/policyCheckHOC'
import ButtonFilter from '@v2/commons/components/FilterButton/FilterButton.component'
import SelectFilter from '@v2/commons/components/FilterSelect/FilterSelect.component'
import { POLICIES } from '@v2/commons/config'
import { useState } from 'react'

import {
  IGetTasksParams,
  TASK_ACTION_OPTIONS,
  TASK_PRIORITY_OPTIONS,
  TASK_STATUS_OPTIONS,
  TASK_SUBJECT_OPTIONS,
  TASK_TYPE_OPTIONS,
  TaskActionEnum,
} from '../../task.types'
import styles from './tasks-filters.module.scss'

interface TaskFilterProps {
  getTasksParamsForm: UseFormReturnType<
    IGetTasksParams,
    (values: IGetTasksParams) => IGetTasksParams
  >
  isProgrammaticFormUpdateRef?: React.MutableRefObject<boolean>
  totalRecords: number
  assignees: { label: string; value: string }[]
  assigneesQueryStatus: { isLoading: boolean; isSuccess: boolean }
  filtersOpen: boolean
  setFiltersOpen: React.Dispatch<React.SetStateAction<boolean>>
}

const TaskTableFilter = ({
  getTasksParamsForm,
  isProgrammaticFormUpdateRef,
  totalRecords,
  assignees,
  filtersOpen,
  setFiltersOpen,
}: TaskFilterProps) => {
  const [subjectOptions, setSubjectOptions] = useState<
    typeof TASK_SUBJECT_OPTIONS
  >([...TASK_SUBJECT_OPTIONS])

  const filterSubjects = (
    value: TaskActionEnum,
    subjects: typeof TASK_SUBJECT_OPTIONS = subjectOptions,
  ) =>
    subjects.map((option) => {
      if (!option.action.includes(value!)) {
        option.disabled = true
      } else {
        option.disabled = false
      }
      return option
    })

  getTasksParamsForm.watch('action', ({ value }) => {
    setSubjectOptions((prev) => {
      const filteredSubjects = filterSubjects(value!, prev)
      const currentSubject = getTasksParamsForm.getValues()['subject']
      const issubjectdInEnabled = !filteredSubjects.find(
        ({ value: subjectValue }) => subjectValue === currentSubject,
      )?.disabled
      getTasksParamsForm.setFieldValue(
        'subject',
        issubjectdInEnabled ? currentSubject : '',
      )
      return filteredSubjects
    })
  })

  const handleClearFilters = () => {
    getTasksParamsForm.reset()
    setSubjectOptions((prev) =>
      prev.map((subject) => {
        subject.disabled = false
        return subject
      }),
    )
  }

  const handleChangeValue = (
    path: keyof IGetTasksParams,
    value: IGetTasksParams[keyof IGetTasksParams],
  ) => {
    getTasksParamsForm.setFieldValue(path, value)
    if (isProgrammaticFormUpdateRef && isProgrammaticFormUpdateRef.current) {
      isProgrammaticFormUpdateRef.current = false
    }
  }

  return (
    <>
      <Flex justify="space-between" align="center" mb="30px">
        <ButtonFilter
          onClick={() => setFiltersOpen((prev) => !prev)}
          isOpen={filtersOpen}
        />
        <Text lh="26px" size="18px">
          Total Records: {totalRecords}
        </Text>
      </Flex>
      {filtersOpen && (
        <form style={{ marginBottom: '30px' }}>
          <Group
            preventGrowOverflow={false}
            grow={true}
            classNames={{ root: styles['group-root'] }}
          >
            <SelectFilter
              options={TASK_ACTION_OPTIONS}
              placeholder="Action"
              {...getTasksParamsForm.getInputProps('action')}
              key={getTasksParamsForm.key('action')}
              onChange={(value: IGetTasksParams['action']) => {
                handleChangeValue('action', value)
              }}
            />
            <SelectFilter
              options={subjectOptions}
              placeholder="Subject"
              {...getTasksParamsForm.getInputProps('subject')}
              key={getTasksParamsForm.key('subject')}
              onChange={(value: IGetTasksParams['subject']) => {
                handleChangeValue('subject', value)
              }}
            />
            <PolicyCheckHOC policyName={POLICIES.IsAllTaskEnabled}>
              <SelectFilter
                options={assignees}
                searchable
                nothingFoundMessage="No users found"
                placeholder="Assigned To"
                {...getTasksParamsForm.getInputProps('assignedToUserId')}
                key={getTasksParamsForm.key('assignedToUserId')}
                onChange={(value: IGetTasksParams['assignedToUserId']) => {
                  handleChangeValue('assignedToUserId', value)
                }}
              />
            </PolicyCheckHOC>
            <SelectFilter
              options={TASK_STATUS_OPTIONS}
              placeholder="Status"
              {...getTasksParamsForm.getInputProps('status')}
              key={getTasksParamsForm.key('status')}
              onChange={(value: IGetTasksParams['status']) => {
                handleChangeValue('status', value)
              }}
            />
            <SelectFilter
              options={TASK_PRIORITY_OPTIONS}
              placeholder="priority"
              {...getTasksParamsForm.getInputProps('priority')}
              key={getTasksParamsForm.key('priority')}
              onChange={(value: IGetTasksParams['priority']) => {
                handleChangeValue('priority', value)
              }}
            />
            <Flex miw="50%" justify="flex-end">
              <Button
                variant="transparent"
                classNames={{ root: styles['clear-button-root'] }}
                onClick={handleClearFilters}
                type="button"
              >
                Clear All
              </Button>
            </Flex>
          </Group>
        </form>
      )}
    </>
  )
}

export default TaskTableFilter
